import {
    Injectable,
    Injector,
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { filterNullOrUndefined } from '@utils/rxjs.utils';

import {
    Setting,
    SettingsAPI,
} from 'main/app-state/current-user/settings';
import * as moment from 'moment';
import {
    combineLatest,
    forkJoin,
    of,
} from 'rxjs';
import {
    catchError,
    filter,
    pairwise,
    pluck,
    repeatWhen,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

import {
    AuthConfigAPI,
    AuthConfigManager,
} from './auth-config';
import {
    CurrentUserAPI,
    CurrentUserManager,
} from './current-user';
import { LanguageAPI } from './language/language.api';
import { LanguageManager } from './language/language.manager';

@Injectable({
    providedIn: 'root',
})
export class AppStateFacade {

    public get user(): CurrentUserAPI {
        return this._currentUser;
    }

    public get authConfig(): AuthConfigAPI {
        return this._authConfig;
    }

    public get settings(): SettingsAPI {
        return this._currentUser.settings;
    }

    public get language(): LanguageAPI {
        return this._language;
    }

    private _isInitialized = false;
    private _authConfig: AuthConfigManager;
    private _currentUser: CurrentUserManager;
    private _language: LanguageManager;

    constructor(
        injector: Injector,
        dateAdapter: DateAdapter<moment.Moment>,
    ) {
        this._authConfig = new AuthConfigManager(injector);
        this._currentUser = new CurrentUserManager(
            injector,
        );
        this._language = new LanguageManager(
            injector,
            this,
            dateAdapter,
        );
    }

    public initialize() {
        if (this._isInitialized) { return of(true); }

        return forkJoin([
            this._authConfig.fetch(),
            this._currentUser.fetch(),
        ])
            .pipe(
                tap(_ => this._isInitialized = true),
                switchMap(() => this._language.initialize()),
                tap(() => this._linkStates()),
                catchError((error) => {
                    error.isSeed = true;
                    throw error;
                }),
            );
    }

    /**
     * Set dependencies between the states of non-hierarchical managers.
     */
    private _linkStates() {
        const currentCultureChange$ = this._currentUser.settings
            .get$(Setting.currentCulture)
            .pipe(
                filter((lang) => !!lang),
            );

        const userLogin$ = this.user.value$.pipe(
            pairwise(),
            filter(([prev, next]) => !prev && !!next),
        );

        const userLogout$ = this.user.value$.pipe(
            pairwise(),
            filter(([prev, next]) => !!prev && !next),
        );

        // State links that are relevant only when the user is logged in.
        combineLatest([
            // Keep the displayed language in sync with the current culture user setting.
            currentCultureChange$.pipe(
                switchMap((lang) => this._language.set(lang!)),
            ),
            this._currentUser.folder.value$.pipe(
                filterNullOrUndefined(),
                // Combine value$ with pairwise to prevent emitting when both Folder and Permissions managers are initialized on login.
                // Using value$ instead of change$ also prevents double GetCurrentPermissions requests when logging out and then back in.
                pairwise(),
                switchMap(() =>
                    this._currentUser.permissions.fetch(),
                ),
            ),
            this._currentUser.permissions.change$.pipe(
                tap(_ => this.user.folderPermissions.flushCache()),
            ),
        ]).pipe(
            // tslint:disable-next-line: rxjs-no-unsafe-takeuntil
            takeUntil(userLogout$),
            repeatWhen(() => userLogin$),
        ).subscribe();

        // State links that don't depend on user auth state.
        userLogout$.pipe(
            switchMap(() => this._authConfig.fetch()),
            pluck('currentCulture'),
            switchMap((lang) => this._language.set(lang)),
        ).subscribe();
    }
}
